import React from "react";
import { Row, Col, Space, Typography, Card } from "antd";
import {
  SafetyOutlined,
  TruckOutlined,
  DollarOutlined,
  WhatsAppOutlined,
  StarFilled,
  CheckCircleFilled,
} from "@ant-design/icons";

const { Text, Title } = Typography;

/**
 * Interface pour un badge de confiance personnalisé
 */
interface CustomBadge {
  icon: React.ReactNode;
  title: string;
  description: string;
  color?: string;
}

/**
 * Props du composant TrustBadges
 */
interface TrustBadgesProps {
  /** Afficher le badge "Satisfait ou Remboursé" (défaut: true) */
  showMoneyBack?: boolean;

  /** Afficher le badge "Livraison Sécurisée" (défaut: true) */
  showDelivery?: boolean;

  /** Afficher le badge "Paiement Sécurisé" (défaut: true) */
  showPayment?: boolean;

  /** Afficher le badge "Service Client WhatsApp" (défaut: true) */
  showWhatsApp?: boolean;

  /** Afficher le badge "Clients Satisfaits" (défaut: true) */
  showReviews?: boolean;

  /** Nombre de clients satisfaits ce mois (pour badge reviews) */
  satisfiedCustomers?: number;

  /** Badges personnalisés additionnels */
  customBadges?: CustomBadge[];

  /** Style du composant ('default' | 'minimal' | 'colorful') */
  variant?: "default" | "minimal" | "colorful";

  /** Affichage en mode compact (icône + titre seulement) */
  compact?: boolean;
}

/**
 * Composant TrustBadges
 *
 * Affiche des badges de confiance pour rassurer les clients
 * (garanties, sécurité, service client, avis positifs)
 *
 * @example
 * // Utilisation standard dans le footer
 * <TrustBadges
 *   satisfiedCustomers={500}
 *   variant="default"
 * />
 *
 * @example
 * // Utilisation minimale avec badges personnalisés
 * <TrustBadges
 *   compact
 *   variant="minimal"
 *   customBadges={[
 *     {
 *       icon: <PhoneOutlined />,
 *       title: "Support 24/7",
 *       description: "Toujours disponible"
 *     }
 *   ]}
 * />
 */
const TrustBadges: React.FC<TrustBadgesProps> = ({
  showMoneyBack = true,
  showDelivery = true,
  showPayment = true,
  showWhatsApp = true,
  showReviews = true,
  satisfiedCustomers = 500,
  customBadges = [],
  variant = "default",
  compact = false,
}) => {
  /**
   * Configuration des couleurs selon le variant
   */
  const colors = {
    default: {
      background: "#fafafa",
      border: "#f0f0f0",
      iconBg: "#f0f0f0",
    },
    minimal: {
      background: "transparent",
      border: "transparent",
      iconBg: "transparent",
    },
    colorful: {
      background: "#fff",
      border: "#e6f7ff",
      iconBg: "#e6f7ff",
    },
  };

  const currentColors = colors[variant];

  /**
   * Badges par défaut
   */
  const defaultBadges: CustomBadge[] = [
    ...(showMoneyBack
      ? [
          {
            icon: <SafetyOutlined style={{ fontSize: 24, color: "#52c41a" }} />,
            title: "Satisfait ou Remboursé",
            description: "Garantie 7 jours",
            color: "#52c41a",
          },
        ]
      : []),
    ...(showDelivery
      ? [
          {
            icon: <TruckOutlined style={{ fontSize: 24, color: "#1890ff" }} />,
            title: "Livraison Sécurisée",
            description: "Suivi en temps réel",
            color: "#1890ff",
          },
        ]
      : []),
    ...(showPayment
      ? [
          {
            icon: <SafetyOutlined style={{ fontSize: 24, color: "#722ed1" }} />,
            title: "Paiement 100% Sécurisé",
            description: "Orange Money, Moov Money",
            color: "#722ed1",
          },
        ]
      : []),
    ...(showWhatsApp
      ? [
          {
            icon: <WhatsAppOutlined style={{ fontSize: 24, color: "#25D366" }} />,
            title: "Service Client WhatsApp",
            description: "Disponible 24/7",
            color: "#25D366",
          },
        ]
      : []),
    ...(showReviews
      ? [
          {
            icon: <StarFilled style={{ fontSize: 24, color: "#faad14" }} />,
            title: `${satisfiedCustomers}+ clients satisfaits`,
            description: "Ce mois-ci",
            color: "#faad14",
          },
        ]
      : []),
  ];

  const allBadges = [...defaultBadges, ...customBadges];

  /**
   * Rendu d'un badge (mode compact)
   */
  const renderBadgeCompact = (badge: CustomBadge, index: number) => (
    <Col key={index} xs={12} sm={8} md={6} lg={4}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          minWidth: 0,
          width: "100%",
          minHeight: 92,
          textAlign: "center",
          padding: "10px 8px",
          borderRadius: 18,
          background: "rgba(255, 255, 255, 0.72)",
          border: "1px solid rgba(15, 39, 71, 0.08)",
        }}
      >
        <div style={{ flexShrink: 0, lineHeight: 1 }}>{badge.icon}</div>
        <Text
          strong
          style={{
            fontSize: 12.5,
            lineHeight: 1.3,
            minWidth: 0,
            overflowWrap: "break-word",
          }}
        >
          {badge.title}
        </Text>
      </div>
    </Col>
  );

  /**
   * Rendu d'un badge (mode complet)
   */
  const renderBadgeFull = (badge: CustomBadge, index: number) => (
    <Col key={index} xs={24} sm={12} md={8} lg={6}>
      <Card
        bordered={variant !== "minimal"}
        style={{
          textAlign: "center",
          backgroundColor: currentColors.background,
          border: `1px solid ${currentColors.border}`,
          borderRadius: 8,
          height: "100%",
        }}
        bodyStyle={{ padding: 16 }}
      >
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: "50%",
              backgroundColor: currentColors.iconBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
            }}
          >
            {badge.icon}
          </div>
          <Text
            strong
            style={{
              fontSize: 14,
              display: "block",
              color: badge.color || "#262626",
            }}
          >
            {badge.title}
          </Text>
          <Text
            type="secondary"
            style={{
              fontSize: 12,
              display: "block",
            }}
          >
            {badge.description}
          </Text>
        </Space>
      </Card>
    </Col>
  );

  return (
    <div
      style={{
        padding: compact ? "8px 0 4px" : "40px 0",
        backgroundColor: variant === "colorful" ? "#fafafa" : "transparent",
      }}
    >
      <Row gutter={[16, 16]} justify="center" align="middle">
        {allBadges.map((badge, index) =>
          compact ? renderBadgeCompact(badge, index) : renderBadgeFull(badge, index)
        )}
      </Row>
    </div>
  );
};

export default TrustBadges;
